import { useCallback, useState } from 'react';
import type { UseCardsStackOptions, UseCardsStackReturn } from './types';

export const useCardsStack = ({
  initialIndex = 0,
  loop = false,
  onIndexChange,
  totalCards,
}: UseCardsStackOptions): UseCardsStackReturn => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  const hasCards = totalCards > 0;
  const canGoNext = hasCards && (loop || activeIndex < totalCards - 1);
  const canGoPrevious = hasCards && (loop || activeIndex > 0);

  const goToIndex = useCallback(
    (index: number) => {
      if (totalCards === 0) {
        return;
      }
      if (index >= 0 && index < totalCards) {
        setActiveIndex((prev) => {
          onIndexChange?.(index, prev);
          return index;
        });
      } else if (loop) {
        const wrappedIndex = ((index % totalCards) + totalCards) % totalCards;
        setActiveIndex((prev) => {
          onIndexChange?.(wrappedIndex, prev);
          return wrappedIndex;
        });
      }
    },
    [loop, onIndexChange, totalCards],
  );

  const goToNext = useCallback(() => {
    if (canGoNext) {
      const nextIndex = loop ? (activeIndex + 1) % totalCards : Math.min(activeIndex + 1, totalCards - 1);
      goToIndex(nextIndex);
    }
  }, [activeIndex, canGoNext, goToIndex, loop, totalCards]);

  const goToPrevious = useCallback(() => {
    if (canGoPrevious) {
      const prevIndex = loop ? (activeIndex - 1 + totalCards) % totalCards : Math.max(activeIndex - 1, 0);
      goToIndex(prevIndex);
    }
  }, [activeIndex, canGoPrevious, goToIndex, loop, totalCards]);

  return {
    activeIndex,
    canGoNext,
    canGoPrevious,
    goToIndex,
    goToNext,
    goToPrevious,
  };
};
